/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IBuyboxAddToCartViewProps,
    IBuyboxProductQuantityViewProps
} from '@msdyn365-commerce-modules/buybox/dist/types';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult } from '@msdyn365-commerce/core';
import * as React from 'react';
import Table from 'reactstrap/lib/Table';
import { ISmweBuyboxViewProps } from './smwe-buybox';

const SmweBuyboxView: React.FC<ISmweBuyboxViewProps> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        quantity,
        title,
        productDescription,
        productTitle,
        productPrice,
        productClubPrice,
        show,
        acclaimRatings,
        cookbookTagline,
        eventSchedule,
        subscriptionButton
        } = props;
    // @ts-ignore
    const tableClass: AsyncResult<string> = props.context.actionContext.get('String', 'Generic-SkuSelectorClasses');
    const isGiftCard = props.data.product && props.data.product.result ? props.data.product.result.ItemId?.toLowerCase() === 'giftcard' : false;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {props.productApellation && (
                    <div>{props.productApellation}</div>
                )}
                {title}
                {productDescription}
                {eventSchedule}
                {acclaimRatings}
                {cookbookTagline}
                {show && _renderTable({
                    addToCart: addToCart,
                    quantity: quantity,
                    productPrice: productPrice,
                    productClubPrice: productClubPrice,
                    tableClass: tableClass,
                    isGiftCard: isGiftCard,
                    productTitle,
                    subscriptionButton
                })}
                {props.content &&
                    props.content.map(
                        (contentSlotData: React.ReactNode, index: number) => {
                         return (
                                <div className='col-12' key={index}>
                                    {contentSlotData}
                                </div>
                            );
                        }
                    )}
            </Node>
        </Module>
    );
};

export interface IRenderTable {
    subscriptionButton?: IBuyboxAddToCartViewProps;
    addToCart: IBuyboxAddToCartViewProps;
    quantity: IBuyboxProductQuantityViewProps | undefined;
    productTitle: React.ReactNode;
    productPrice: React.ReactNode;
    productClubPrice: React.ReactNode;
    isGiftCard: boolean;
    tableClass: AsyncResult<string>;
}

const _renderTable = (props: IRenderTable) => {
    const { addToCart, quantity, productTitle, productPrice, productClubPrice, tableClass, isGiftCard, subscriptionButton } = props;

    return (
        <div className={`sku-selector-container row ${tableClass && tableClass.result}`}>
        <Table borderless responsive size='lg' role='presentation'>
            <thead>
                <tr>
                    {productTitle && <th scope='col' className='sku-selector-product-title'>Product</th>}
                    {productPrice && <th scope='col' className={`sku-selector-product-price ${isGiftCard ? 'price-width-adjust' : ''}`}>Price</th>}
                    {!isGiftCard && productClubPrice && <th scope='col' className='sku-selector-product-price'>Club</th>}
                    {quantity && <th scope='col' className='sku-selector-product-quantity'>QTY</th>}
                    {addToCart && <th scope='col' className='sku-selector-add-to-cart' />}
                </tr>
            </thead>
            <tbody>
                <tr>
                    {productTitle && <td className='sku-selector-product-title'>{productTitle}</td>}
                    {productPrice && <td className={`sku-selector-product-price ${isGiftCard ? 'price-width-adjust' : ''}`}>{productPrice}</td>}
                    {!isGiftCard && productClubPrice && <td className='sku-selector-product-price'>{productClubPrice}</td>}
                    {quantity && <td className={`sku-selector-product-quantity ${isGiftCard ? 'unselectable' : ''}`}>{_renderQuantity(quantity)}</td>}
                    {addToCart && <td className='sku-selector-add-to-cart'>{_renderAddToCart(addToCart)}</td>}
                    {subscriptionButton && <td className='sku-selector-add-to-cart'>{_renderAddToCart(subscriptionButton)}</td>}
                </tr>
            </tbody>
        </Table>
    </div>
);};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {errors}
            </Node>
            {input}
        </Node>
    );
};

export default SmweBuyboxView;
